import { useParams, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import posts from "../data/blog.json";
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from "lucide-react";
import { InfinityLoop } from "../components/Illustrations";

const AUDIENCE_LABEL = {
  candidates: "For candidates",
  employers: "For employers",
  market: "Market",
};

function Block({ block }) {
  if (block.type === "p") {
    return <p className="font-body text-lg text-ink-700 leading-relaxed">{block.text}</p>;
  }
  if (block.type === "h2") {
    return (
      <h2 className="font-headings text-3xl md:text-4xl text-ink tracking-tight leading-tight mt-4">
        {block.text}
      </h2>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="space-y-3 font-body text-lg text-ink-700 leading-relaxed">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3">
            <span aria-hidden className="mt-3 h-1.5 w-1.5 rounded-full bg-ink flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "cta") {
    return (
      <div className="bg-stone text-cream rounded-2xl p-8 md:p-10 my-6 relative overflow-hidden">
        <InfinityLoop className="absolute -right-8 -top-6 w-72 h-32 opacity-20" stroke="#C2FFE1" strokeWidth={4} />
        <h3 className="font-headings text-2xl md:text-3xl text-mint leading-tight relative">
          {block.heading}
        </h3>
        <p className="mt-3 font-body text-cream/85 leading-relaxed relative">{block.body}</p>
        <Link
          to={block.to}
          data-testid="blog-post-cta"
          className="inline-flex items-center gap-2 mt-6 bg-mint text-ink hover:bg-mint-deep px-6 py-3 rounded-full font-body font-medium text-sm relative"
        >
          {block.label} <ArrowUpRight size={16} />
        </Link>
      </div>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/help" replace />;
  }

  const audienceLabel = AUDIENCE_LABEL[post.audience] || post.audience;
  const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-NZ", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const relatedPosts = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div data-testid="page-blog-post">
      <Seo
        title={post.title}
        description={post.excerpt}
        image="/og-brand.png"
        path={`/help/${post.slug}`}
        type="article"
      />

      {/* Header */}
      <section className="pt-32 md:pt-44 pb-12 md:pb-16 bg-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/help"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-sm font-body font-medium text-stone-dark hover:text-ink mb-10"
          >
            <ArrowLeft size={16} /> Back to all posts
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-8 text-xs uppercase tracking-[0.25em] font-medium">
            <span className="bg-mint text-ink px-3 py-1 rounded-full">{audienceLabel}</span>
            <span className="text-stone-dark">{post.topic}</span>
          </div>
          <h1
            data-testid="blog-post-title"
            className="font-headings text-4xl sm:text-5xl md:text-6xl text-ink tracking-tight leading-[1.05]"
          >
            {post.title}
          </h1>
          <p className="mt-6 font-headings text-xl md:text-2xl text-ink-700 leading-snug italic">
            {post.excerpt}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm font-body text-stone-dark border-t border-ink-100 pt-6">
            <span className="flex items-center gap-2">
              <Calendar size={14} strokeWidth={1.5} /> {formattedDate}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} strokeWidth={1.5} /> {post.readingMinutes} min read
            </span>
            <span>By {post.author}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20 md:pb-28 bg-cream">
        <article className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 space-y-6" data-testid="blog-post-body">
          {post.body.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </article>
      </section>

      {/* Related */}
      {relatedPosts.length > 0 && (
        <section className="py-16 md:py-24 bg-mint-soft border-t border-ink-100" data-testid="blog-related">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-8">
              Keep reading
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/help/${p.slug}`}
                  className="bg-white p-8 rounded-2xl border border-ink-100 hover:shadow-md hover:-translate-y-1 transition-all group flex flex-col"
                >
                  <span className="text-[10px] uppercase tracking-[0.25em] text-stone-dark mb-4">
                    {AUDIENCE_LABEL[p.audience]} · {p.readingMinutes} min read
                  </span>
                  <h3 className="font-headings text-2xl text-ink group-hover:text-stone-dark transition-colors leading-tight">
                    {p.title}
                  </h3>
                  <p className="font-body text-sm text-ink-700 leading-relaxed mt-3">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
